import type { Ref, WritableComputedRef } from "vue";
import type { ISwiperState } from "./swiperState";
import { STATUS_CONSTANTS } from "./statusConstants";
import type { ISwiperConfig } from "~/types/swipe-bet/config";
import type { ISwiperProps } from "~/types/swipe-bet/swiper";
import type { ISwiperContent } from "~/types/swipe-bet/content";

type SwiperContentWithKey = ISwiperContent & {
	$vtKey?: string;
	[key: string]: unknown;
};

const getItemKey = (item: SwiperContentWithKey, keyName: string): string => {
	const value = item[keyName];
	return typeof value === "string" ? value : item.swipeBetId || "";
};

const difference = (array: string[], exclude: string[]): string[] => {
	const result: string[] = [];
	for (let i = 0; i < array.length; i++) {
		if (exclude.includes(array[i])) {
			break;
		}
		result.push(array[i]);
	}
	return result;
};

const useSwiperQueue = (
	swiperConfig: Ref<ISwiperConfig>,
	swiperProps: ISwiperProps,
	swiperState: Ref<ISwiperState>,
	listItems: Ref<SwiperContentWithKey[]>,
	queue: WritableComputedRef<SwiperContentWithKey[]>
) => {
	const diff = (list: string[], old: string[]) => {
		const keyName = swiperProps.keyName || "key";
		const add = difference(list, old);
		let onceRewindCount = 0;

		// Handle additions/rewinds
		if (add.length) {
			for (let i = 0; i < add.length; i++) {
				const item = queue.value[i];
				if (item && getItemKey(item, keyName) && getItemKey(item, keyName)) {
					onceRewindCount++;
					const id = getItemKey(item, keyName);
					const newVueTinderkey = id + Math.random();

					// Check if this item was previously leaving or rewinding
					if (
						(item.$vtKey && swiperConfig.value.leavingKeys.includes(item.$vtKey)) ||
						swiperConfig.value.leavingKeys.includes(id) ||
						(item.$vtKey && swiperConfig.value.rewindKeys.includes(item.$vtKey)) ||
						swiperConfig.value.rewindKeys.includes(id)
					) {
						// Give it a new key to avoid DOM reuse issues
						item.$vtKey = newVueTinderkey;

						// Update the rewind keys array
						const rewindIndex = Math.max(swiperConfig.value.rewindKeys.indexOf(item.$vtKey), swiperConfig.value.rewindKeys.indexOf(id));
						if (rewindIndex > -1) {
							swiperConfig.value.rewindKeys[rewindIndex] = newVueTinderkey;
							swiperState.value.status = STATUS_CONSTANTS.REWINDING;
						}
					}
				} else {
					break;
				}
			}
		}
		swiperConfig.value.onceRewindCount = onceRewindCount;

		// Handle removals
		const remove = difference(old, list);
		if (remove.length) {
			// Add the first item to leaving keys (the one being removed)
			if (listItems.value[0]) {
				const firstItemKey = listItems.value[0].$vtKey || getItemKey(listItems.value[0], keyName);
				swiperConfig.value.leavingKeys.push(firstItemKey);
			}

			// Handle items that need new keys to avoid conflicts
			for (let i = (swiperProps?.max || 3) + 1; i < (swiperProps?.max || 3) + 1 + remove.length; i++) {
				const item = listItems.value[i];
				if (item) {
					const itemKey = getItemKey(item, keyName);
					if (swiperConfig.value.leavingKeys.includes(itemKey) || swiperConfig.value.hidingKeys.includes(itemKey)) {
						item.$vtKey = itemKey + Math.random();
					}
				}
			}
		}

		// Update the list
		listItems.value = JSON.parse(JSON.stringify(queue.value));
	};

	return {
		diff,
	};
};

export default useSwiperQueue;
